import React, { useState } from "react";
import {
  Link,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ArrowRight, Building2, Search, ShieldCheck } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import {
  AGENCY_PATH,
  CLIENT_TAB_KEYS,
  LIMITED_ACCESS_PATH,
  agencyClientPath,
  clientSelectionPath,
  resolveClientTool,
  safeProductionReturnTo,
} from "../../utils/agencyNavigation";
import "./ClientContextGate.css";

/** This boundary runs before creator pages mount or issue tenant requests. */
export function ClientContextGate() {
  const { isAgencyStaff, activeClientId } = useAuth();
  const location = useLocation();
  if (!isAgencyStaff) return <Navigate to={LIMITED_ACCESS_PATH} replace />;
  if (!activeClientId)
    return (
      <Navigate
        to={clientSelectionPath(
          `${location.pathname}${location.search}${location.hash}`,
        )}
        replace
      />
    );
  return <Outlet />;
}

export function LimitedAccessPage() {
  const { logout } = useAuth();
  return (
    <section className="client-context-page client-context-limited">
      <ShieldCheck size={28} aria-hidden="true" />
      <p className="client-context-eyebrow">Acceso de solo lectura</p>
      <h1>Tu cuenta necesita acceso al equipo</h1>
      <p>
        Para trabajar con clientes, un administrador debe habilitar tu acceso al
        equipo. Si revisas entregas como cliente, solicita tu acceso al portal.
      </p>
      <button type="button" className="client-context-link" onClick={logout}>
        Cerrar sesión
      </button>
    </section>
  );
}

export function ClientSelectionPage() {
  const { isAgencyStaff, isAgencyAdmin, organizations, switchOrganization } =
    useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  if (!isAgencyStaff) return <Navigate to={LIMITED_ACCESS_PATH} replace />;
  const returnTo = safeProductionReturnTo(
    new URLSearchParams(location.search).get("returnTo"),
  );
  const tool = returnTo ? resolveClientTool(returnTo.split(/[?#]/)[0]) : null;
  const clients = organizations.filter((org) => org.agency_client_id);
  const matches = clients.filter((org) =>
    org.name
      ?.toLocaleLowerCase("es")
      .includes(search.trim().toLocaleLowerCase("es")),
  );
  const requestedTab = new URLSearchParams(location.search).get("tab");
  const clientTab = CLIENT_TAB_KEYS.includes(requestedTab)
    ? requestedTab
    : "production";
  const choose = (org) => {
    // The API wrapper reads this selection synchronously on the next mount.
    switchOrganization(org.id);
    navigate(returnTo || agencyClientPath(org.agency_client_id, clientTab), {
      replace: true,
    });
  };
  return (
    <section className="client-context-page">
      <p className="client-context-eyebrow">Producción por cliente</p>
      <h1>¿Para quién vamos a crear?</h1>
      <p>
        {tool
          ? `Selecciona un cliente para abrir ${tool.label.toLocaleLowerCase("es")}.`
          : "Selecciona el cliente con el que quieres trabajar."}{" "}
        Cada espacio reúne su marca, campañas, entregas y créditos.
      </p>
      {clients.length > 0 ? (
        <>
          <label className="client-context-search">
            <Search size={17} aria-hidden="true" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              type="search"
              placeholder="Buscar cliente"
              aria-label="Buscar cliente"
            />
          </label>
          <div className="client-context-options">
            {matches.map((org) => (
              <button key={org.id} type="button" onClick={() => choose(org)}>
                <span className="client-context-monogram" aria-hidden="true">
                  {org.name?.charAt(0) || <Building2 size={21} />}
                </span>
                <span>
                  <strong>{org.name}</strong>
                  <small>Abrir espacio de trabajo</small>
                </span>
                <ArrowRight size={18} aria-hidden="true" />
              </button>
            ))}
          </div>
          {matches.length === 0 && (
            <p className="client-context-empty">
              No encontramos clientes con ese nombre.
            </p>
          )}
        </>
      ) : (
        <p className="client-context-empty">
          {isAgencyAdmin
            ? "Todavía no hay clientes. Crea el primero desde Clientes para empezar a producir."
            : "Todavía no hay clientes disponibles. Un administrador puede crear el primero para el equipo."}
        </p>
      )}
      <Link className="client-context-link" to={AGENCY_PATH}>
        Ir a la cartera de clientes <ArrowRight size={15} aria-hidden="true" />
      </Link>
    </section>
  );
}
